'use client';
import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Search, User, Building2, ChevronRight, Trophy, MapPin, X } from 'lucide-react';
import Link from 'next/link';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { createClient } from '@/lib/supabase/client';

interface UserResult {
  id: string;
  full_name: string;
  race_name?: string | null;
  role: string;
  avatar_url: string | null;
  organization_name?: string;
}

interface TrackResult {
  id: string;
  track_name: string;
  track_address: string;
  avatar_url: string | null;
  is_open: boolean;
}

type DropdownItem =
  | { type: 'driver'; data: UserResult }
  | { type: 'organizer'; data: UserResult }
  | { type: 'track'; data: TrackResult };

export default function SearchPage() {
  const supabase = createClient();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<UserResult[]>([]);
  const [trackResults, setTrackResults] = useState<TrackResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [dropdownItems, setDropdownItems] = useState<DropdownItem[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [dropdownLoading, setDropdownLoading] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (
        dropdownRef.current && !dropdownRef.current.contains(e.target as Node) &&
        inputRef.current && !inputRef.current.contains(e.target as Node)
      ) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const runSearch = useCallback(async (q: string) => {
    if (!q.trim()) {
      setDropdownItems([]);
      setShowDropdown(false);
      return;
    }
    setDropdownLoading(true);
    try {
      const [driverRes, orgRes, adminRes, trackRes] = await Promise.all([
        supabase.from('user_profiles').select('id, full_name, race_name, role, avatar_url, organization_name')
          .or(`full_name.ilike.%${q.trim()}%,race_name.ilike.%${q.trim()}%`)
          .not('role', 'eq', 'organizer').not('role', 'eq', 'admin').not('role', 'eq', 'track_manager')
          .order('full_name', { ascending: true }).limit(5),
        supabase.from('user_profiles').select('id, full_name, race_name, role, avatar_url, organization_name')
          .eq('role', 'organizer').ilike('organization_name', `%${q.trim()}%`)
          .order('organization_name', { ascending: true }).limit(5),
        supabase.from('user_profiles').select('id, full_name, race_name, role, avatar_url, organization_name')
          .eq('role', 'admin').or(`full_name.ilike.%${q.trim()}%,race_name.ilike.%${q.trim()}%`)
          .order('full_name', { ascending: true }).limit(3),
        supabase.from('track_profiles').select('id, track_name, track_address, avatar_url, is_open')
          .or(`track_name.ilike.%${q.trim()}%,track_address.ilike.%${q.trim()}%`)
          .order('track_name', { ascending: true }).limit(5),
      ]);
      const items: DropdownItem[] = [
        ...((driverRes.data || []) as UserResult[]).map(d => ({ type: 'driver' as const, data: d })),
        ...((orgRes.data || []) as UserResult[]).map(o => ({ type: 'organizer' as const, data: o })),
        ...((adminRes.data || []) as UserResult[]).map(a => ({ type: 'driver' as const, data: a })),
        ...((trackRes.data || []) as TrackResult[]).map(t => ({ type: 'track' as const, data: t })),
      ];
      setDropdownItems(items);
      setShowDropdown(true);
    } finally {
      setDropdownLoading(false);
    }
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setQuery(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!val.trim()) {
      setDropdownItems([]);
      setShowDropdown(false);
      return;
    }
    debounceRef.current = setTimeout(() => runSearch(val), 250);
  };

  const handleSearch = useCallback(async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!query.trim()) return;
    setShowDropdown(false);
    setLoading(true);
    setSearched(true);
    try {
      const [driverData, orgData, adminData, tracksData] = await Promise.all([
        supabase.from('user_profiles').select('id, full_name, race_name, role, avatar_url, organization_name')
          .or(`full_name.ilike.%${query.trim()}%,race_name.ilike.%${query.trim()}%`)
          .not('role', 'eq', 'organizer').not('role', 'eq', 'admin').not('role', 'eq', 'track_manager')
          .order('full_name', { ascending: true }).limit(20),
        supabase.from('user_profiles').select('id, full_name, race_name, role, avatar_url, organization_name')
          .eq('role', 'organizer').ilike('organization_name', `%${query.trim()}%`)
          .order('organization_name', { ascending: true }).limit(20),
        supabase.from('user_profiles').select('id, full_name, race_name, role, avatar_url, organization_name')
          .eq('role', 'admin').or(`full_name.ilike.%${query.trim()}%,race_name.ilike.%${query.trim()}%`)
          .order('full_name', { ascending: true }).limit(10),
        supabase.from('track_profiles').select('id, track_name, track_address, avatar_url, is_open')
          .or(`track_name.ilike.%${query.trim()}%,track_address.ilike.%${query.trim()}%`)
          .order('track_name', { ascending: true }).limit(20),
      ]);
      setResults([
        ...((driverData.data || []) as UserResult[]),
        ...((orgData.data || []) as UserResult[]),
        ...((adminData.data || []) as UserResult[]),
      ]);
      setTrackResults((tracksData.data || []) as TrackResult[]);
    } finally {
      setLoading(false);
    }
  }, [query]);

  const drivers = results.filter(r => r.role !== 'organizer' && r.role !== 'admin' && r.role !== 'track_manager');
  const organizers = results.filter(r => r.role === 'organizer');

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Navbar />
      <main className="flex-1 pt-16">
        <div className="max-w-3xl mx-auto px-4 py-8 sm:py-12">
          <div className="mb-6 sm:mb-8">
            <p className="text-xs font-bold uppercase tracking-widest text-primary mb-2">Community</p>
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold uppercase tracking-tight text-foreground mb-2">Search</h1>
            <p className="text-muted-foreground text-sm">Find drivers, organizers, and tracks on SpeedPass.</p>
          </div>

          {/* Google-style search bar with autocomplete */}
          <form onSubmit={handleSearch} className="mb-8">
            <div className="relative">
              <div className="relative flex items-center">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground z-10 pointer-events-none" />
                <input
                  ref={inputRef}
                  type="text"
                  value={query}
                  onChange={handleInputChange}
                  onFocus={() => { if (dropdownItems.length > 0) setShowDropdown(true); }}
                  onKeyDown={e => { if (e.key === 'Enter') { handleSearch(); } if (e.key === 'Escape') setShowDropdown(false); }}
                  placeholder="Search drivers, organizers, or tracks..."
                  className="w-full pl-9 pr-10 py-3.5 rounded-xl bg-input border border-border text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary/60 transition-all"
                  autoComplete="off"
                />
                {query && (
                  <button
                    type="button"
                    onClick={() => { setQuery(''); setDropdownItems([]); setShowDropdown(false); setResults([]); setTrackResults([]); inputRef.current?.focus(); }}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <X size={15} />
                  </button>
                )}
              </div>

              {/* Autocomplete Dropdown */}
              {showDropdown && (
                <div ref={dropdownRef} className="absolute top-full left-0 right-0 mt-1 bg-card border border-border rounded-xl shadow-2xl z-50 overflow-hidden max-h-80 overflow-y-auto">
                  {dropdownLoading ? (
                    <div className="flex items-center justify-center py-4">
                      <span className="w-5 h-5 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
                    </div>
                  ) : dropdownItems.length === 0 ? (
                    <div className="px-4 py-3 text-sm text-muted-foreground text-center">No results found</div>
                  ) : (
                    <div>
                      {dropdownItems.map((item, idx) => {
                        if (item.type === 'driver') {
                          return (
                            <Link
                              key={`dd-driver-${item.data.id}`}
                              href={`/driver/${item.data.id}`}
                              onClick={() => setShowDropdown(false)}
                              className="flex items-center gap-3 px-4 py-3 hover:bg-muted/50 transition-colors border-b border-border/50 last:border-0"
                            >
                              <div className="w-8 h-8 rounded-full bg-primary/10 border border-primary/20 flex items-center justify-center shrink-0 overflow-hidden">
                                {item.data.avatar_url ? <img src={item.data.avatar_url} alt={item.data.race_name || item.data.full_name} className="w-full h-full object-cover" /> : <span className="text-xs font-bold text-primary">{((item.data.race_name || item.data.full_name) || 'U').charAt(0).toUpperCase()}</span>}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-semibold text-foreground truncate">{item.data.race_name || item.data.full_name}</p>
                                <p className="text-xs text-muted-foreground">Driver</p>
                              </div>
                              <User size={13} className="text-muted-foreground shrink-0" />
                            </Link>
                          );
                        }
                        if (item.type === 'organizer') {
                          return (
                            <Link
                              key={`dd-org-${item.data.id}`}
                              href={`/organizer-profile/${item.data.id}`}
                              onClick={() => setShowDropdown(false)}
                              className="flex items-center gap-3 px-4 py-3 hover:bg-muted/50 transition-colors border-b border-border/50 last:border-0"
                            >
                              <div className="w-8 h-8 rounded-full bg-primary/10 border border-primary/20 flex items-center justify-center shrink-0 overflow-hidden">
                                {item.data.avatar_url ? <img src={item.data.avatar_url} alt={item.data.organization_name || 'Organizer'} className="w-full h-full object-cover" /> : <Building2 size={14} className="text-primary" />}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-semibold text-foreground truncate">{(item.data.organization_name || item.data.full_name).toUpperCase()}</p>
                                <p className="text-xs text-muted-foreground">Organizer</p>
                              </div>
                              <Building2 size={13} className="text-muted-foreground shrink-0" />
                            </Link>
                          );
                        }
                        if (item.type === 'track') {
                          return (
                            <Link
                              key={`dd-track-${item.data.id}`}
                              href={`/track/${item.data.id}`}
                              onClick={() => setShowDropdown(false)}
                              className="flex items-center gap-3 px-4 py-3 hover:bg-muted/50 transition-colors border-b border-border/50 last:border-0"
                            >
                              <div className="w-8 h-8 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center shrink-0 overflow-hidden">
                                {item.data.avatar_url ? <img src={item.data.avatar_url} alt={item.data.track_name} className="w-full h-full object-cover" /> : <MapPin size={14} className="text-primary" />}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-semibold text-foreground truncate uppercase">{item.data.track_name}</p>
                                <p className="text-xs text-muted-foreground truncate">{item.data.track_address}</p>
                              </div>
                              <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded shrink-0 ${item.data.is_open ? 'bg-positive/10 text-positive' : 'bg-negative/10 text-negative'}`}>
                                {item.data.is_open ? 'OPEN' : 'CLOSED'}
                              </span>
                            </Link>
                          );
                        }
                        return null;
                      })}
                      <button
                        type="submit"
                        className="w-full flex items-center justify-center gap-2 px-4 py-3 text-xs font-bold uppercase tracking-wide text-primary hover:bg-primary/5 transition-colors border-t border-border"
                        onClick={() => handleSearch()}
                      >
                        <Search size={13} />See all results for &quot;{query}&quot;
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </form>

          {loading && (
            <div className="flex items-center justify-center py-16">
              <span className="w-8 h-8 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
            </div>
          )}

          {!loading && searched && results.length === 0 && trackResults.length === 0 && (
            <div className="bg-card border border-border rounded-xl p-12 text-center">
              <Search size={40} className="text-muted-foreground mx-auto mb-3" />
              <p className="text-sm font-semibold text-muted-foreground">No results found</p>
              <p className="text-xs text-muted-foreground mt-1">Try a different name or spelling.</p>
            </div>
          )}

          {!loading && searched && (results.length > 0 || trackResults.length > 0) && (
            <div className="space-y-8">
              {drivers.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <User size={16} className="text-primary" />
                    <h2 className="text-sm font-bold uppercase tracking-widest text-primary">Drivers ({drivers.length})</h2>
                  </div>
                  <div className="space-y-2">
                    {drivers.map(driver => (
                      <Link key={driver.id} href={`/driver/${driver.id}`} className="flex items-center gap-4 bg-card border border-border rounded-xl px-4 sm:px-5 py-4 hover:border-primary/40 hover:bg-primary/5 transition-all group">
                        <div className="w-10 h-10 rounded-full bg-primary/10 border border-primary/20 flex items-center justify-center shrink-0 overflow-hidden">
                          {driver.avatar_url ? <img src={driver.avatar_url} alt={driver.race_name || driver.full_name} className="w-full h-full object-cover" loading="lazy" /> : <span className="text-sm font-bold text-primary">{((driver.race_name || driver.full_name) || 'U').charAt(0).toUpperCase()}</span>}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-bold text-foreground">{driver.race_name || driver.full_name}</p>
                          <p className="text-xs text-muted-foreground capitalize">Driver</p>
                        </div>
                        <div className="hidden sm:flex items-center gap-1.5 text-xs font-semibold text-primary opacity-0 group-hover:opacity-100 transition-opacity">
                          View Stats <ChevronRight size={14} />
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {organizers.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <Building2 size={16} className="text-primary" />
                    <h2 className="text-sm font-bold uppercase tracking-widest text-primary">Organizers ({organizers.length})</h2>
                  </div>
                  <div className="space-y-2">
                    {organizers.map(org => (
                      <Link key={org.id} href={`/organizer-profile/${org.id}`} className="flex items-center gap-4 bg-card border border-border rounded-xl px-4 sm:px-5 py-4 hover:border-primary/40 hover:bg-primary/5 transition-all group">
                        <div className="w-10 h-10 rounded-full bg-primary/10 border border-primary/20 flex items-center justify-center shrink-0 overflow-hidden">
                          {org.avatar_url ? <img src={org.avatar_url} alt={org.organization_name || 'Organizer'} className="w-full h-full object-cover" loading="lazy" /> : <Building2 size={18} className="text-primary" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-bold text-foreground">{(org.organization_name || org.full_name).toUpperCase()}</p>
                          <p className="text-xs text-muted-foreground">Organizer</p>
                        </div>
                        <div className="hidden sm:flex items-center gap-1.5 text-xs font-semibold text-primary opacity-0 group-hover:opacity-100 transition-opacity">
                          View Events <ChevronRight size={14} />
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {trackResults.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <MapPin size={16} className="text-primary" />
                    <h2 className="text-sm font-bold uppercase tracking-widest text-primary">Tracks ({trackResults.length})</h2>
                  </div>
                  <div className="space-y-2">
                    {trackResults.map(track => (
                      <Link key={track.id} href={`/track/${track.id}`} className="flex items-center gap-4 bg-card border border-border rounded-xl px-4 sm:px-5 py-4 hover:border-primary/40 hover:bg-primary/5 transition-all group">
                        <div className="w-10 h-10 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center shrink-0 overflow-hidden">
                          {track.avatar_url ? <img src={track.avatar_url} alt={track.track_name} className="w-full h-full object-cover" loading="lazy" /> : <MapPin size={18} className="text-primary" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-bold text-foreground uppercase">{track.track_name}</p>
                          {track.track_address && <p className="text-xs text-muted-foreground truncate">{track.track_address}</p>}
                        </div>
                        <div className="flex flex-col items-end gap-1.5 shrink-0">
                          <span className={`px-2 py-0.5 rounded text-xs font-bold ${track.is_open ? 'bg-positive/10 text-positive' : 'bg-negative/10 text-negative'}`}>
                            {track.is_open ? 'OPEN' : 'CLOSED'}
                          </span>
                          <span className="hidden sm:flex items-center gap-1 text-xs font-semibold text-primary opacity-0 group-hover:opacity-100 transition-opacity">
                            View Track <ChevronRight size={13} />
                          </span>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {!searched && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4">
              <div className="bg-card border border-border rounded-xl p-5">
                <div className="flex items-center gap-2 mb-2">
                  <Trophy size={16} className="text-primary" />
                  <p className="text-xs font-bold uppercase tracking-wider text-primary">Driver Search</p>
                </div>
                <p className="text-sm text-muted-foreground">Search for any driver by their full name to view their race statistics and event history.</p>
              </div>
              <div className="bg-card border border-border rounded-xl p-5">
                <div className="flex items-center gap-2 mb-2">
                  <Building2 size={16} className="text-primary" />
                  <p className="text-xs font-bold uppercase tracking-wider text-primary">Organizer Search</p>
                </div>
                <p className="text-sm text-muted-foreground">Search for event organizers by their organization name to view their events.</p>
              </div>
              <div className="bg-card border border-border rounded-xl p-5">
                <div className="flex items-center gap-2 mb-2">
                  <MapPin size={16} className="text-primary" />
                  <p className="text-xs font-bold uppercase tracking-wider text-primary">Track Search</p>
                </div>
                <p className="text-sm text-muted-foreground">Search for registered karting tracks by name or location and view their profiles.</p>
              </div>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
